// The octopus mark comes alive (MOTION.md, Part 2). Every `.logo-animated` says hello once when it first
// shows, then rests; `.logo-idle` (error pages, empty states) keeps idle life on a loop and looks around.
import { flow, loopClock, oneShotClock, seeded, track, wave } from "~/js/utils/motion.js";

// Logo units (viewBox 24) and ms.
const DIP = 0.5; // tentacles only dip: moving up would show their cut ends inside the badge
const DIP_KEYS = [
	[0, 0],
	[300, 1],
	[720, 0, "out"],
];
const STAGGER = 90; // between neighbouring tentacles
const BLINK_KEYS = [
	[0, 1],
	[110, 0.15],
	[150, 0.15],
	[340, 1, "out"],
];

const HELLO_MS = 1900;
// The head rises while the tentacles ripple below it.
const HELLO_LIFT = [
	[0, 0],
	[450, 0.4],
	[1100, 0.1],
	[1600, 0],
];
const HELLO_BLINK = 1500;

const IDLE_MS = 8000;
const IDLE_BREATH = 4000;
const IDLE_LIFT = 0.3;
// [start, right to left]
const IDLE_RIPPLES = [
	[300, false],
	[4300, true],
];
const IDLE_BLINKS = [2800, 6500, 6900]; // the last two make a double blink
// Gaze: left, then right with a blink on the turn, then down at the page's text or button; back with the double blink.
const LOOK_X = [
	[0, 0],
	[1200, 0],
	[1700, -0.35],
	[2900, -0.35],
	[3400, 0.35],
	[4700, 0.35],
	[5200, 0],
];
const LOOK_Y = [
	[0, 0],
	[4700, 0],
	[5200, 0.3],
	[6500, 0.3],
	[7000, 0],
];
const PUPIL_R = 0.55; // inside an eye white of r 1, with room for the gaze

/** How far tentacle `i` (0 = left) dips in a ripple starting at `start`. */
const ripple = (t, start, i, reverse = false) =>
	DIP *
	(0.85 + 0.3 * seeded(i)) *
	track(t - start - STAGGER * (reverse ? 6 - i : i) - 30 * seeded(i, 1), DIP_KEYS);

// The idle mark is big enough for pupils: each one sits on its eye and blinks with it. Centres come from the
// eye circles' attributes, not getBBox(), which is empty while a mark is not rendered.
function addPupils(eyes) {
	return eyes.map((eye) => {
		const pupil = document.createElementNS("http://www.w3.org/2000/svg", "circle");
		pupil.setAttribute("r", PUPIL_R);
		pupil.setAttribute("class", "fill-slate-900");
		eye.after(pupil);
		return { pupil, cx: eye.cx.baseVal.value, cy: eye.cy.baseVal.value };
	});
}

function animate(svg) {
	const idle = svg.classList.contains("logo-idle");
	const tentacles = [...svg.querySelectorAll(".tentacle")];
	const head = svg.querySelector(".head");
	const eyes = [...svg.querySelectorAll(".eye")];
	const pupils = idle ? addPupils(eyes) : [];
	const pose = (dip, lift, lid, lookX = 0, lookY = 0) => {
		tentacles.forEach((tentacle, i) =>
			tentacle.setAttribute("transform", `translate(0 ${dip(i)})`),
		);
		head.setAttribute("transform", `translate(0 ${-lift})`);
		for (const eye of eyes) eye.style.transform = `scaleY(${lid})`;
		for (const { pupil, cx, cy } of pupils) {
			pupil.setAttribute(
				"transform",
				`translate(${cx} ${cy}) scale(1 ${lid}) translate(${lookX} ${lookY})`,
			);
		}
	};

	if (idle) {
		loopClock(svg, IDLE_MS, (t) =>
			pose(
				(i) =>
					IDLE_RIPPLES.reduce((sum, [start, reverse]) => sum + ripple(t, start, i, reverse), 0),
				(IDLE_LIFT * (1 - wave(t, IDLE_BREATH, 0.25))) / 2,
				Math.min(...IDLE_BLINKS.map((start) => track(t - start, BLINK_KEYS))),
				track(t, LOOK_X),
				track(t, LOOK_Y),
			),
		);
	} else {
		oneShotClock(svg, HELLO_MS, (t) =>
			pose((i) => ripple(t, 0, i), flow(t, HELLO_LIFT), track(t - HELLO_BLINK, BLINK_KEYS)),
		);
	}
}

for (const svg of document.querySelectorAll(".logo-animated")) animate(svg);
